import { ConfigDataType, Portfolio, TradeDate, UserDataType } from '../../types';

export interface PendingTradeDateAccount {
    accountId: string;
    unallocated: number;
    unsent: number;
}

export interface PendingTradeDate {
    dateId: string; // dd-MM-yyyy
    totalRows: number; // trades on the date (scoped to the given accounts)
    allocated: number; // fully-allocated trades
    unallocated: number; // trades not fully allocated
    unsent: number; // trades not LOCKED (not sent to Geneva yet)
    accounts: PendingTradeDateAccount[]; // per-account pending breakdown (only accounts with pending work)
}

/**
 * Scans trade_dates docs' per-account stats for dates that still need work
 * (unallocated or not-yet-sent trades), so nothing on an unopened date is forgotten.
 * accounts = null → all accounts (admin/oper); otherwise only the given accounts.
 * Newest first.
 */
export const getPendingTradeDates = (
    tradeDates: Record<string, TradeDate>,
    accounts: string[] | null,
): PendingTradeDate[] => {
    const toTime = (dateId: string) => {
        const [dd, mm, yyyy] = dateId.split('-').map(Number);
        return new Date(yyyy, mm - 1, dd).getTime();
    };
    return Object.entries(tradeDates)
        .map(([dateId, data]) => {
            let totalRows = 0;
            let allocated = 0;
            let unsent = 0;
            const accountRows: PendingTradeDateAccount[] = [];
            for (const [accountId, stats] of Object.entries(data.stats ?? {})) {
                if (accounts && !accounts.includes(accountId)) continue;
                const total = stats.totalRows ?? 0;
                const accountAllocated = Math.min(total, stats.allocatedTrades ?? 0);
                const accountUnsent = Math.max(0, total - (stats.states?.LOCKED ?? 0));
                totalRows += total;
                allocated += accountAllocated;
                unsent += accountUnsent;
                if (total - accountAllocated > 0 || accountUnsent > 0) {
                    accountRows.push({ accountId, unallocated: total - accountAllocated, unsent: accountUnsent });
                }
            }
            accountRows.sort((a, b) => a.accountId.localeCompare(b.accountId));
            return { dateId, totalRows, allocated, unallocated: Math.max(0, totalRows - allocated), unsent, accounts: accountRows };
        })
        .filter((d) => d.unallocated > 0 || d.unsent > 0)
        .sort((a, b) => toTime(b.dateId) - toTime(a.dateId));
};

/**
 * The type used to classify a portfolio against config/tradesMap accounts:
 * portfolioContract (e.g. DPM_GLOBAL) wins when present, else portfolioTypeCode.
 */
export const getEffectivePortfolioType = (portfolio: Portfolio): string | undefined =>
    portfolio.portfolioContract ?? portfolio.portfolioTypeCode;

/**
 * Returns the union of portfolioTypeCode AND portfolioContract values across all
 * of the user's availableTradeAccounts, based on config/tradesMap — i.e. the
 * allowed *effective* portfolio types (see getEffectivePortfolioType).
 * Handles the legacy case where portfolioTypeCode is a single string instead of an array.
 */
export const getAllowedPortfolioTypeCodes = (
    userContext: UserDataType,
    configContext: ConfigDataType,
): string[] => {
    const portfolioTypeCodes: string[] = [];

    if (userContext.availableTradeAccounts) {
        for (const account of userContext.availableTradeAccounts) {
            const accountConfig = configContext.tradesMap?.accounts?.[account];
            const portfolioTypeCode = accountConfig?.portfolioTypeCode;
            if (Array.isArray(portfolioTypeCode)) {
                portfolioTypeCodes.push(...portfolioTypeCode.filter((code) => code !== undefined));
            } else if (portfolioTypeCode !== undefined) {
                portfolioTypeCodes.push(portfolioTypeCode);
            }
            portfolioTypeCodes.push(...(accountConfig?.portfolioContract ?? []).filter((code) => code !== undefined));
        }
    }

    // dedupe while preserving order
    return [...new Set(portfolioTypeCodes)];
};
